import 'babel-polyfill';
import 'remodal';
import React from 'react';
import $ from 'jquery';
import { Provider as ReduxProvider } from 'react-redux';
import { QueryClientProvider } from 'react-query';
import './polyfills';
import SquireUnavailable from './app/SquireUnavailable';
import createRoutes from './routes';
import { checkUser, getApiStatus } from './app/auth/actions';
import { history, Notifications, store } from './main';
import config from './config';
import './hacks';
import { FullstoryService } from './modules/infra/observability/fullstory';
import { SentryService } from './modules/infra/observability/sentry';
import OverlaySpinner from './app/common/OverlaySpinner';
import { buildFlagsContextFromUser, FeatureFlagsProvider } from './modules/infra/feature-flags';
import { ReduxStoreBridge } from './modules/infra/redux-bridge';
import { persistentKVStore } from './modules/infra/persistence';
import { queryClient } from './queryClient';
import { RouterBridge } from './modules/infra/routing';
import { initHttpInterceptors } from './modules/infra/http-interceptors';
import { DatadogService } from './modules/infra/observability/datadog';
import { Logger } from './modules/infra/observability/logger';
import { ChurnZeroService } from './modules/infra/observability/churnzero';
import { MixpanelTracker } from './modules/infra/observability/mixpanel';
import { PostHogTracker } from './modules/infra/observability/posthog';
import { BreakpointObserver } from './modules/infra/dls/breakpoints/observer';
import { ObservabilitySessionTrackers } from './modules/infra/observability/common/observability-session-trackers';
import { LanguageProvider } from './modules/infra/i18n';

// problem loading with es6 syntax,
// doesn't recognize jQuery
window.jQuery = $;
require('bootstrap');
require('bootstrap-datepicker');
require('bootstrap-datepicker/dist/locales/bootstrap-datepicker.es.min');

ReduxStoreBridge.configure(store);
RouterBridge.configure(history);
FullstoryService.init({ orgId: config.analytics.Fullstory.orgId });
SentryService.init({
  sentryDsn: config.sentry.dsn,
  sentrySlug: config.sentry.slug,
  environment: config.deploymentEnv,
  fsApi: FullstoryService.getApi(),
});
ChurnZeroService.init({
  environment: config.deploymentEnv,
  appKey: config.analytics.ChurnZero.appKey,
});
initHttpInterceptors();

DatadogService.init({
  ...config.datadog,
  environment: config.deploymentEnv,
  version: config.version,
});
MixpanelTracker.init();
PostHogTracker.init();

if (DatadogService.getLogger()) {
  Logger.addLogger(DatadogService.getLogger());
}

const routes = createRoutes(store, history);

class App extends React.Component {
  // eslint-disable-next-line react/state-in-constructor
  state = { isStatusChecked: false, isApiChecked: false, isApiUp: true, ticker: null };

  // eslint-disable-next-line react/sort-comp
  checkApiStatus() {
    getApiStatus().then(({ ok }) => {
      this.setState({
        isApiChecked: true,
        isApiUp: ok,
        // eslint-disable-next-line react/no-access-state-in-setstate
        ticker: ok ? null : (this.state.ticker || 0) + 1,
      });
    });
  }

  componentDidMount() {
    this.checkApiStatus();

    window.addEventListener('navigate-parent-app', this.navigateFromChildApp);
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.isApiChecked && this.state.ticker !== prevState.ticker && !this.state.isApiUp) {
      setTimeout(() => this.checkApiStatus(), Math.min(this.state.ticker, 30) * 1000);
    }

    if (this.state.isApiUp && !prevState.isStatusChecked && !this.state.isStatusChecked) {
      const action = checkUser({
        fromInit: true,
        shopId: persistentKVStore.getItem('shop'),
      });

      store.dispatch(action).then(() => {
        this.setState({ isStatusChecked: true });
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('navigate-parent-app', this.navigateFromChildApp);
  }

  // eslint-disable-next-line class-methods-use-this
  navigateFromChildApp = (event) => {
    const route = event.detail;

    history.push(route);
  };

  render() {
    if (!this.state.isApiChecked) {
      return <OverlaySpinner />;
    }

    if (!this.state.isApiUp) {
      return <SquireUnavailable />;
    }

    if (!this.state.isStatusChecked) {
      return null;
    }

    const state = store.getState();
    const flagsContext = buildFlagsContextFromUser(state.auth.user, state.auth.activeShop, state.auth.barberUser) || {};

    return (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <FeatureFlagsProvider {...flagsContext} clientSideId={config.launchDarkly.clientSideId}>
        <QueryClientProvider client={queryClient}>
          <Notifications>
            <ReduxProvider store={store}>
              <LanguageProvider>
                <BreakpointObserver />
                <ObservabilitySessionTrackers />
                {routes}
              </LanguageProvider>
            </ReduxProvider>
          </Notifications>
        </QueryClientProvider>
      </FeatureFlagsProvider>
    );
  }
}

// Wrap the App component with withLanguageRerender to enable automatic re-renders
// when the language changes. This allows all child components to use the global t() function.
export default App;
